import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { IAIWorkspace, useCreateAiWorkspace, useSearchAiWorkspace } from "@entities/aiWorkspace";
import { Button, Input, Modal, Table } from "antd";
import { format } from "date-fns";
import { ArrowRight, FolderOpen, Sparkles, User } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PROJECT_AI_MODULES } from "./modules";
import styles from "./styles.module.scss";

export const AIWorkspacePage: React.FC = () => {
  const [query, setQuery] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const navigate = useNavigate();

  const { data: searchResponse, isFetching: isLoading } = useSearchAiWorkspace({
    pageNo,
    limit: pageSize,
    name: query || undefined,
  });

  const pendingModuleRef = React.useRef<string | null>(null);

  const { create: createProject, isCreating, isCreated } = useCreateAiWorkspace((res) => {
    setIsModalOpen(false);
    setNewProjectName("");
    const newProjectId = res.data._id;
    const pendingModule = pendingModuleRef.current;
    if (pendingModule) {
      navigate(`/ai-workspace/${newProjectId}?module=${pendingModule}`);
      pendingModuleRef.current = null;
    } else {
      navigate(`/ai-workspace/${newProjectId}`);
    }
  });

  useEffect(() => {
    if (isCreated) {
      setIsModalOpen(false);
      setNewProjectName("");
    }
  }, [isCreated]);

  const projects = searchResponse?.data?.data || [];
  const pagination = searchResponse?.data?.pagination;

  useEffect(() => {
    setPageNo(1);
  }, [query, pageSize]);

  const handleCreateProject = () => {
    if (!newProjectName.trim()) return;
    createProject({ name: newProjectName });
  };

  const startModuleProject = (mod: typeof PROJECT_AI_MODULES[number]) => {
    pendingModuleRef.current = mod.id;
    const dateLabel = format(new Date(), "MMM d");
    createProject({ name: `${mod.title} · ${dateLabel}` });
  };

  const columns = [
    {
      title: "PROJECT",
      dataIndex: "name",
      key: "name",
      render: (text: string, record: IAIWorkspace) => (
        <div className={styles.projectNameCell}>
          <div className={styles.projectIcon}>
            <FolderOpen size={14} />
          </div>
          <div className={styles.projectNameWrapper}>
            <span className={styles.projectNameText}>{text}</span>
          </div>
        </div>
      ),
    },
    {
      title: "SOURCE",
      dataIndex: "createdByType",
      key: "createdByType",
      render: (type: string) => (
        <span className={styles.sourceBadge}>
          <User size={11} />
          {type || "user"}
        </span>
      ),
    },
    {
      title: "UPDATED",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (date: string) => (
        <span className={styles.updatedDateText}>
          {date ? format(new Date(date), "MMM d, yyyy") : "-"}
        </span>
      ),
    },
  ];

  return (
    <div className={styles.pageContainer}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.badge}>
            <Sparkles size={15} />
            <span>AI Workspace</span>
          </div>
          <h1>AI workspace</h1>
          <p>
            Projects use Creator Fit, Script Vet, and Draft Vet — each with one master prompt. Open
            an existing project or start from a check module below.
          </p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setIsModalOpen(true)}
          className={styles.primaryButton}
        >
          Create new project
        </Button>
      </header>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2>Start with a module</h2>
          <span>Creates a new project</span>
        </div>
        <div className={styles.modulesGrid}>
          {PROJECT_AI_MODULES.map((mod) => {
            const Icon = mod.icon;
            return (
              <div
                key={mod.id}
                className={styles.moduleCard}
                onClick={() => startModuleProject(mod)}
              >
                <div className={styles.cardTopRow}>
                  <div className={styles.iconWrapper}>
                    <Icon size={17} />
                  </div>
                  <span className={styles.phaseBadge}>{mod.phase}</span>
                </div>
                <div className={styles.moduleContent}>
                  <div className={styles.moduleHeaderRow}>
                    <span className={styles.moduleTitle}>{mod.title}</span>
                    <ArrowRight size={14} className={styles.arrowIcon} />
                  </div>
                  <p className={styles.moduleDesc}>{mod.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <div style={{width: 250}}>
            <h2>Recent projects</h2>
            <p className={styles.projectCount}>
              {projects.length} project{projects.length !== 1 ? "s" : ""}
            </p>
          </div>
          <Input
            prefix={<SearchOutlined style={{ color: "#a3acb9" }} />}
            placeholder="Search projects…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className={styles.tableSearch}
          />
        </div>

        <div className={styles.tableContainer}>
          <Table
            dataSource={projects}
            columns={columns}
            rowKey="_id"
            onRow={(record) => ({
              onClick: () => {
                navigate(`/ai-workspace/${record._id}`);
              },
              style: { cursor: "pointer" },
            })}
            pagination={{
              current: pageNo,
              pageSize: pageSize,
              total: pagination?.totalCount || 0,
              onChange: (page, size) => {
                setPageNo(page);
                setPageSize(size);
              },
            }}
            loading={isLoading}
            locale={{
              emptyText: (
                <div className={styles.emptyState}>
                  <div className={styles.emptyIcon}>
                    <FolderOpen size={18} />
                  </div>
                  <h3>{query.trim() ? "No matching projects" : "No projects yet"}</h3>
                  <p>
                    {query.trim()
                      ? "Try a different search term."
                      : "Click a module above to create a new project and start working."}
                  </p>
                </div>
              ),
            }}
          />
        </div>
      </section>

      <Modal
        title="Create new project"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsModalOpen(false)}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={isCreating}
            onClick={handleCreateProject}
            className={styles.primaryButtonModal}
          >
            Create
          </Button>,
        ]}
      >
        <div className={styles.modalInputWrapper}>
          <label className={styles.modalInputLabel}>
            Project Name
          </label>
          <Input
            placeholder="e.g. Summer Campaign"
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
            onPressEnter={handleCreateProject}
          />
        </div>
      </Modal>
    </div>
  );
};
